import React, { useEffect, useState } from 'react';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Place, RouteLeg } from '../types';

export function getPlaceType(place: Place): string {
  if (place.found_for_preference) {
    return place.found_for_preference.charAt(0).toUpperCase() + place.found_for_preference.slice(1);
  }
  if (place.types && place.types.length > 0) {
    const specificTypes = place.types.filter(
      (type) =>
        !type.includes('establishment') &&
        !type.includes('point_of_interest') &&
        type !== 'geocode' &&
        type !== 'route' &&
        type !== 'locality' &&
        type !== 'political'
    );
    if (specificTypes.length > 0) {
      return specificTypes[0]
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return place.types[0]
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  return 'Place';
}

export function formatDirectionsTime(d: Date): string {
  try {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export function estimateStayMinutesForPlaceType(placeTypeStr: string, placeName: string): number {
  const t = (placeTypeStr || '').toLowerCase();
  const n = (placeName || '').toLowerCase();

  if (t.includes('museum') || t.includes('gallery') || t.includes('attraction')) return 90;
  if (t.includes('park') || t.includes('garden') || t.includes('zoo')) return 75;
  if (t.includes('theater') || t.includes('cinema') || t.includes('movie')) return 120;

  if (t.includes('restaurant') || t.includes('diner')) return 75;
  if (t.includes('cafe') || t.includes('coffee')) return 40;
  if (t.includes('bar') || t.includes('pub')) return 60;

  if (t.includes('gym') || t.includes('fitness') || t.includes('yoga')) return 60;
  if (t.includes('supermarket') || t.includes('grocery')) return 30;
  if (t.includes('shopping')) return 75;
  if (t.includes('pharmacy')) return 15;
  if (t.includes('gas') || t.includes('fuel')) return 10;
  if (t.includes('bank') || t.includes('atm')) return 10;

  if (n.includes('museum')) return 90;
  if (n.includes('park')) return 75;
  if (n.includes('cafe') || n.includes('coffee')) return 40;
  if (n.includes('restaurant')) return 75;

  return 45;
}

function getPlaceEmojiForStop(place: Place, placeType: string): string {
  const key = `${placeType} ${(place.found_for_preference || '')} ${(place.types || []).join(' ')}`.toLowerCase();
  if (key.includes('coffee') || key.includes('cafe')) return '☕';
  // Keep grocery/supermarket checks before restaurant/food checks.
  if (
    key.includes('supermarket') ||
    key.includes('grocery') ||
    key.includes('grocer') ||
    key.includes('food_store') ||
    key.includes('convenience_store')
  ) {
    return '🛒';
  }
  if (key.includes('restaurant') || key.includes('food') || key.includes('dining')) return '🍽️';
  if (key.includes('bar') || key.includes('pub')) return '🍺';
  if (key.includes('post') || key.includes('postal') || key.includes('post_office')) return '📮';
  if (key.includes('landmark') || key.includes('historic') || key.includes('historical') || key.includes('monument')) return '🏰';
  if (key.includes('gas') || key.includes('fuel') || key.includes('petrol') || key.includes('service_station')) return '⛽';
  if (key.includes('pharmacy') || key.includes('drugstore') || key.includes('chemist')) return '💊';
  if (key.includes('museum') || key.includes('art') || key.includes('gallery')) return '🏛️';
  if (key.includes('park') || key.includes('garden') || key.includes('nature')) return '🌳';
  if (key.includes('shopping') || key.includes('mall') || key.includes('store') || key.includes('market')) return '🛍️';
  if (key.includes('hotel') || key.includes('stay') || key.includes('accommodation')) return '🏨';
  if (key.includes('gym') || key.includes('fitness') || key.includes('sport')) return '🏋️';
  if (key.includes('spa') || key.includes('wellness')) return '💆';
  if (key.includes('beach') || key.includes('sea')) return '🏖️';
  if (key.includes('library') || key.includes('book')) return '📚';
  if (key.includes('movie') || key.includes('cinema') || key.includes('theatre') || key.includes('theater')) return '🎬';
  if (key.includes('music') || key.includes('concert')) return '🎵';
  return '📍';
}

function isBookablePlace(place: Place, placeType: string): boolean {
  const key = `${placeType} ${(place.found_for_preference || '')} ${(place.types || []).join(' ')}`.toLowerCase();
  return (
    key.includes('restaurant') ||
    key.includes('dining') ||
    key.includes('hotel') ||
    key.includes('accommodation') ||
    key.includes('lodging')
  );
}

export type StopEtaInfo = {
  etaText?: string;
  estStayText?: string;
  estStayMinutes?: number;
};

export function buildEtaByStopId(
  suggestedPlaces: Place[],
  activeLegs: RouteLeg[] | undefined,
  stayOverridesByStopId: Record<string, number>,
  directionsStartTimeMode: 'now' | 'custom',
  directionsStartDateTime: string
): Record<string, StopEtaInfo> {
  const etaByStopId: Record<string, StopEtaInfo> = {};
  if (suggestedPlaces.length === 0 || !activeLegs || activeLegs.length === 0) {
    return etaByStopId;
  }

  const startTime =
    directionsStartTimeMode === 'custom' && directionsStartDateTime
      ? new Date(directionsStartDateTime)
      : new Date();

  let cumulativeTravelSeconds = 0;
  let cumulativeStaySeconds = 0;
  suggestedPlaces.forEach((p, idx) => {
    const leg = activeLegs[idx];
    const legSeconds = leg?.duration?.value ?? 0;
    const stopId = p.place_id || `stop-${idx}`;
    const defaultEstMin = estimateStayMinutesForPlaceType(getPlaceType(p), p.name);
    const override = stayOverridesByStopId[stopId];
    const estMin =
      override != null && Number.isFinite(override) ? Math.max(0, override) : defaultEstMin;

    cumulativeTravelSeconds += legSeconds;
    const etaSeconds = cumulativeTravelSeconds + cumulativeStaySeconds;
    const eta = new Date(startTime.getTime() + etaSeconds * 1000);

    cumulativeStaySeconds += Math.max(0, Math.round(estMin * 60));

    etaByStopId[stopId] = {
      etaText: formatDirectionsTime(eta),
      estStayMinutes: estMin,
      estStayText: estMin ? `~${Math.round(estMin / 5) * 5} min` : undefined,
    };
  });
  return etaByStopId;
}

// Sortable stop row (uses @dnd-kit). Drag from handle; click content to open place details.
function SortableStopRow({
  id,
  place,
  displayIndex,
  placeType,
  etaText,
  estStayMinutes,
  estStayText,
  showBookingActions = true,
  showPlaceType = true,
  onStayMinutesChange,
  onPlaceClick,
}: {
  id: string;
  place: Place;
  displayIndex: number;
  placeType: string;
  etaText?: string;
  estStayMinutes?: number;
  estStayText?: string;
  showBookingActions?: boolean;
  showPlaceType?: boolean;
  onStayMinutesChange?: (minutes: number) => void;
  onPlaceClick?: (place: Place) => void;
}) {
  const [isEditingStay, setIsEditingStay] = useState(false);
  const [stayDraft, setStayDraft] = useState<string>('');

  useEffect(() => {
    if (!isEditingStay) return;
    if (estStayMinutes != null && Number.isFinite(estStayMinutes)) {
      setStayDraft(String(estStayMinutes));
      return;
    }
    if (estStayText) {
      const m = estStayText.match(/(\d+)/);
      setStayDraft(m ? m[1] : '');
      return;
    }
    setStayDraft('');
  }, [isEditingStay, estStayMinutes, estStayText]);

  const bookingStorageKey = `routex.bookingStatus.${place.place_id || id}`;
  const [bookingStatus, setBookingStatus] = useState<'idle' | 'loading' | 'success' | 'failed'>(() => {
    try {
      const saved = window.localStorage.getItem(bookingStorageKey);
      if (saved === 'success' || saved === 'failed' || saved === 'idle') return saved;
      return 'idle';
    } catch {
      return 'idle';
    }
  });

  useEffect(() => {
    if (bookingStatus === 'loading') return;
    try {
      window.localStorage.setItem(bookingStorageKey, bookingStatus);
    } catch {
      // ignore
    }
  }, [bookingStatus, bookingStorageKey]);

  const handleAutomaticBooking = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (bookingStatus === 'loading') return;

    if (bookingStatus === 'success') {
      setBookingStatus('idle');
      return;
    }

    try {
      setBookingStatus('loading');
      await new Promise((resolve) => setTimeout(resolve, 1200));
      const ok = Math.random() < 0.85;
      setBookingStatus(ok ? 'success' : 'failed');
    } catch {
      setBookingStatus('failed');
    }
  };

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const stopEmoji = getPlaceEmojiForStop(place, placeType);
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-xl border p-4 select-none ${
        isDragging ? 'opacity-50 border-blue-400 shadow-lg z-10' : 'border-gray-200'
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          className="material-symbols-outlined text-gray-400 flex-shrink-0 mt-0.5 cursor-grab active:cursor-grabbing touch-none"
          style={{ touchAction: 'none' }}
          aria-hidden
          {...attributes}
          {...listeners}
        >
          drag_indicator
        </span>
        <div className="flex-shrink-0 mt-0.5 flex flex-col items-center min-w-[2.25rem]">
          <span
            className="h-8 w-8 rounded-full border border-blue-200 bg-white flex items-center justify-center text-[16px]"
            title={placeType}
          >
            {stopEmoji}
          </span>
          <span className="mt-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-800 text-[11px] font-medium">
            {place.rating != null ? place.rating.toFixed(1) : '—'}
            <span className="material-symbols-outlined text-amber-600 text-[12px] leading-none">star</span>
          </span>
        </div>
        <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">
          {displayIndex + 1}
        </div>
        <button
          type="button"
          onClick={() => onPlaceClick?.(place)}
          className="flex-1 min-w-0 text-left cursor-pointer hover:opacity-80 transition-opacity rounded-lg -m-1 p-1"
        >
          <p className="text-gray-900 font-medium">{place.name}</p>
          {place.formatted_address && (
            <p className="text-sm text-gray-500 mt-1 truncate">{place.formatted_address}</p>
          )}
          {showPlaceType && (
            <p className="text-sm text-gray-400 mt-1">({placeType})</p>
          )}
        </button>

        <div className="flex-shrink-0 mt-0.5 text-right">
          {showBookingActions && (
            <button
              type="button"
              onClick={handleAutomaticBooking}
              disabled={bookingStatus === 'loading'}
              className={[
                'px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors',
                'disabled:opacity-80 disabled:cursor-not-allowed',
                bookingStatus === 'idle'
                  ? 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
                  : '',
                bookingStatus === 'loading' ? 'bg-gray-100 text-gray-700 border-gray-200' : '',
                bookingStatus === 'success'
                  ? 'bg-green-100 text-green-700 border-green-200'
                  : '',
                bookingStatus === 'failed'
                  ? 'bg-red-100 text-red-700 border-red-200 hover:bg-red-200'
                  : '',
              ].join(' ')}
            >
              <span className="inline-flex items-center gap-2">
                {bookingStatus === 'loading' && (
                  <span
                    className="inline-block h-3.5 w-3.5 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin"
                    aria-label="Processing"
                  />
                )}
                {bookingStatus === 'idle' && 'Automatic Booking'}
                {bookingStatus === 'loading' && 'Processing...'}
                {bookingStatus === 'success' && 'Booked!'}
                {bookingStatus === 'failed' && 'Failed!'}
              </span>
            </button>
          )}

          {(etaText || estStayText) && (
            <div className={`${showBookingActions ? 'mt-1' : ''}`}>
              {etaText && (
                <div className="text-[11px] font-semibold text-gray-700">ETA {etaText}</div>
              )}
              {estStayText && (
                <div className="text-[11px] text-gray-500">
                  <span className="mr-1">Estimated stay</span>
                  {!isEditingStay ? (
                    <button
                      type="button"
                      className="underline decoration-dotted underline-offset-2 hover:text-gray-700"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsEditingStay(true);
                      }}
                      title="Click to edit stay duration"
                    >
                      {estStayText}
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      <input
                        type="number"
                        min={0}
                        step={5}
                        value={stayDraft}
                        onChange={(e) => setStayDraft(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const v = Number(stayDraft);
                            if (Number.isFinite(v)) onStayMinutesChange?.(Math.max(0, v));
                            setIsEditingStay(false);
                          }
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            setIsEditingStay(false);
                          }
                        }}
                        onBlur={() => {
                          const v = Number(stayDraft);
                          if (Number.isFinite(v)) onStayMinutesChange?.(Math.max(0, v));
                          setIsEditingStay(false);
                        }}
                        className="w-14 px-1 py-0.5 rounded border border-gray-200 text-[11px] text-gray-700 bg-white"
                        autoFocus
                      />
                      <span className="text-[11px] text-gray-500">min</span>
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export type DirectionsRouteStopsVariant = 'full' | 'embedded';

export interface DirectionsStartTimeRibbonProps {
  activeRouteSuggestion: number;
  directionsStartTimeMode: 'now' | 'custom';
  directionsStartDateTime: string;
  onDirectionsStartTimeModeChange: (mode: 'now' | 'custom') => void;
  onDirectionsStartDateTimeChange: (value: string) => void;
  onCalendarOpenChange?: (isOpen: boolean) => void;
}

/** Start time (Now / Choose + datetime) — used in the route details panel above the start location. */
export const DirectionsStartTimeRibbon: React.FC<DirectionsStartTimeRibbonProps> = ({
  activeRouteSuggestion,
  directionsStartTimeMode,
  directionsStartDateTime,
  onDirectionsStartTimeModeChange,
  onDirectionsStartDateTimeChange,
  onCalendarOpenChange,
}) => {
  const dateTimeInputRef = React.useRef<HTMLInputElement>(null);
  const [isCalendarActive, setIsCalendarActive] = React.useState(false);
  if (activeRouteSuggestion === -1) return null;
  return (
    <div className="flex-shrink-0 flex w-full flex-row flex-wrap items-center justify-start gap-2">
      <span className="text-xs font-semibold text-gray-600 whitespace-nowrap">Start time</span>
      <div className="inline-flex max-w-full rounded-lg border border-gray-200 overflow-hidden">
        <button
          type="button"
          onClick={() => onDirectionsStartTimeModeChange('now')}
          className={`px-3 py-1.5 text-sm font-semibold ${
            directionsStartTimeMode === 'now'
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          Now
        </button>
        <button
          type="button"
          onClick={() => onDirectionsStartTimeModeChange('custom')}
          className={`px-3 py-1.5 text-sm font-semibold ${
            directionsStartTimeMode === 'custom'
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          Choose
        </button>
      </div>
      {directionsStartTimeMode === 'custom' && (
        <div className="w-full rounded-lg border border-gray-200 bg-gray-100/95 p-1.5 sm:w-auto">
          <div className="relative">
            {isCalendarActive && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setIsCalendarActive(false);
                  onCalendarOpenChange?.(false);
                  dateTimeInputRef.current?.blur();
                }}
                className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-md bg-blue-600 px-2 py-1 text-xs font-semibold text-white hover:bg-blue-700"
              >
                Done
              </button>
            )}
            <input
              ref={dateTimeInputRef}
              type="datetime-local"
              value={directionsStartDateTime}
              onChange={(e) => onDirectionsStartDateTimeChange(e.target.value)}
              onClick={() => {
                setIsCalendarActive(true);
                onCalendarOpenChange?.(true);
              }}
              onMouseDown={() => {
                setIsCalendarActive(true);
                onCalendarOpenChange?.(true);
              }}
              onTouchStart={() => {
                setIsCalendarActive(true);
                onCalendarOpenChange?.(true);
              }}
              onFocus={() => {
                setIsCalendarActive(true);
                onCalendarOpenChange?.(true);
              }}
              onBlur={() => {
                setIsCalendarActive(false);
                onCalendarOpenChange?.(false);
              }}
              className={`w-full min-w-0 max-w-full rounded-md border border-gray-300 bg-white py-1.5 pr-2 text-sm text-gray-800 sm:w-auto sm:max-w-[220px] ${
                isCalendarActive ? 'pl-16' : 'px-2'
              }`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export interface DirectionsRouteStopsBlockProps {
  variant: DirectionsRouteStopsVariant;
  startLocation: string;
  endLocation: string;
  suggestedPlaces: Place[];
  activeRouteSuggestion: number;
  directionsStartTimeMode: 'now' | 'custom';
  directionsStartDateTime: string;
  onDirectionsStartTimeModeChange: (mode: 'now' | 'custom') => void;
  onDirectionsStartDateTimeChange: (value: string) => void;
  etaByStopId: Record<string, StopEtaInfo>;
  setStayOverridesByStopId: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  directionsSensors: NonNullable<React.ComponentProps<typeof DndContext>['sensors']>;
  onDragEnd: (event: DragEndEvent) => void;
  onPlaceClick: (place: Place) => void;
}

export const DirectionsRouteStopsBlock: React.FC<DirectionsRouteStopsBlockProps> = ({
  variant,
  startLocation,
  endLocation,
  suggestedPlaces,
  activeRouteSuggestion,
  directionsStartTimeMode,
  directionsStartDateTime,
  onDirectionsStartTimeModeChange,
  onDirectionsStartDateTimeChange,
  etaByStopId,
  setStayOverridesByStopId,
  directionsSensors,
  onDragEnd,
  onPlaceClick,
}) => {
  const showStartTime = activeRouteSuggestion !== -1;
  const showBookingActions = activeRouteSuggestion !== -1;
  const showPlaceType = variant === 'full';

  const startTimeControls = showStartTime && (
    <DirectionsStartTimeRibbon
      activeRouteSuggestion={activeRouteSuggestion}
      directionsStartTimeMode={directionsStartTimeMode}
      directionsStartDateTime={directionsStartDateTime}
      onDirectionsStartTimeModeChange={onDirectionsStartTimeModeChange}
      onDirectionsStartDateTimeChange={onDirectionsStartDateTimeChange}
    />
  );

  const stopsList = (
    <DndContext
      sensors={directionsSensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
      autoScroll
    >
      <SortableContext
        items={suggestedPlaces.map((p, i) => p.place_id || `stop-${i}`)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-3">
          {suggestedPlaces.map((place, index) => (
            (() => {
              const placeType = getPlaceType(place);
              return (
            <SortableStopRow
              key={place.place_id || `stop-${index}`}
              id={place.place_id || `stop-${index}`}
              place={place}
              displayIndex={index}
              placeType={placeType}
              etaText={etaByStopId[place.place_id || `stop-${index}`]?.etaText}
              estStayMinutes={etaByStopId[place.place_id || `stop-${index}`]?.estStayMinutes}
              estStayText={etaByStopId[place.place_id || `stop-${index}`]?.estStayText}
              showBookingActions={showBookingActions && isBookablePlace(place, placeType)}
              showPlaceType={showPlaceType}
              onStayMinutesChange={(minutes) => {
                const stopId = place.place_id || `stop-${index}`;
                setStayOverridesByStopId((prev) => ({ ...prev, [stopId]: minutes }));
              }}
              onPlaceClick={onPlaceClick}
            />
              );
            })()
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );

  if (variant === 'embedded') {
    return <div className="space-y-3">{stopsList}</div>;
  }

  return (
    <div className="space-y-3">
      {startLocation && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-start gap-3">
            <span className="material-symbols-outlined text-green-600 text-[24px] mt-0.5">place</span>
            <div className="flex-1 min-w-0 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-500 mb-1">Start</p>
                <p className="text-gray-900">{startLocation}</p>
              </div>
              {startTimeControls}
            </div>
          </div>
        </div>
      )}

      {suggestedPlaces.length === 0 && <div className="mb-4" />}

      {stopsList}

      {endLocation && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-start gap-3">
            <span className="material-symbols-outlined text-red-600 text-[24px] mt-0.5">place</span>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-500 mb-1">Destination</p>
              <p className="text-gray-900">{endLocation}</p>
            </div>
          </div>
        </div>
      )}

      {!startLocation && !endLocation && suggestedPlaces.length === 0 && (
        <div className="text-center py-12">
          <span className="material-symbols-outlined text-gray-300 text-6xl mb-4">directions</span>
          <p className="text-gray-500">No route planned yet</p>
          <p className="text-sm text-gray-400 mt-2">Plan a route to see directions here</p>
        </div>
      )}
    </div>
  );
};
